import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    MessageCircle,
    Send,
    Paperclip,
    Search,
    Circle,
    Check,
    CheckCheck,
    Image as ImageIcon,
    File,
    Download,
    Users,
    ArrowLeft,
    Mic
} from 'lucide-react';
import { toast } from 'sonner';
import { VoiceRecorder, VoiceMessagePlayer, TextToSpeechButton } from '../components/VoiceComponents';
import { linkifyText } from '../utils/linkify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role labels in French
const ROLE_LABELS = {
    admin: 'Admin',
    super_admin: 'Super Admin',
    vendeur: 'Vendeur',
    confirmateur: 'Confirmateur',
    driver: 'Livreur',
    office_agent: 'Agent'
};

// Role colors
const ROLE_COLORS = {
    admin: 'bg-purple-100 text-purple-800',
    super_admin: 'bg-red-100 text-red-800',
    vendeur: 'bg-blue-100 text-blue-800',
    confirmateur: 'bg-green-100 text-green-800',
    driver: 'bg-orange-100 text-orange-800',
    office_agent: 'bg-gray-100 text-gray-800'
};

const Messages = () => {
    const { user } = useAuth();
    const [token, setToken] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [typingUser, setTypingUser] = useState(null);
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [showContactList, setShowContactList] = useState(true);
    
    // Get token from localStorage
    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        setToken(storedToken);
    }, [user]);
    
    const wsRef = useRef(null);
    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const fileInputRef = useRef(null);
    
    // Handle responsive
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobileView(mobile);
            if (!mobile) setShowContactList(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Auto-scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    useEffect(() => {
        scrollToBottom();
    }, [messages]);
    
    // Filter contacts by search
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredContacts(contacts);
        } else {
            const query = searchQuery.toLowerCase();
            setFilteredContacts(contacts.filter(c => 
                c.name?.toLowerCase().includes(query) ||
                c.email?.toLowerCase().includes(query) ||
                ROLE_LABELS[c.role]?.toLowerCase().includes(query)
            ));
        }
    }, [searchQuery, contacts]);
    
    // Fetch contacts
    const fetchContacts = useCallback(async () => {
        if (!token) return;
        try {
            const res = await fetch(`${API_URL}/api/chat/contacts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setContacts(data.contacts || []);
                setOnlineUsers(data.online_users || []);
            }
        } catch (err) {
            console.error('Error fetching contacts:', err);
        }
    }, [token]);
    
    // Fetch messages
    const fetchMessages = useCallback(async (contactId) => {
        if (!token || !contactId) return;
        try {
            const res = await fetch(`${API_URL}/api/chat/messages/${contactId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setMessages(data.messages || []);
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
        }
    }, [token]);
    
    // Connect WebSocket
    useEffect(() => {
        if (!token) return;
        
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsHost = API_URL.replace('https://', '').replace('http://', '');
        const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${token}`;
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
            console.log('Chat WebSocket connected');
        };
        
        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            
            if (data.type === 'connection_established') {
                setOnlineUsers(data.online_users || []);
            }
            else if (data.type === 'new_message') {
                const msg = data.message;
                if (selectedContact && msg.sender_id === selectedContact.id) {
                    setMessages(prev => [...prev, msg]);
                    ws.send(JSON.stringify({
                        type: 'read',
                        conversation_id: msg.conversation_id,
                        other_user_id: msg.sender_id
                    }));
                } else {
                    // Update contact's unread count
                    setContacts(prev => prev.map(c => 
                        c.id === msg.sender_id 
                            ? { ...c, unread_count: (c.unread_count || 0) + 1, last_message: msg.content, last_message_at: msg.created_at }
                            : c
                    ));
                    toast.info(`Nouveau message de ${msg.sender_name}`);
                }
            }
            else if (data.type === 'typing') {
                if (selectedContact && data.sender_id === selectedContact.id) {
                    setTypingUser(data.sender_name);
                    setIsTyping(true);
                    setTimeout(() => setIsTyping(false), 3000);
                }
            }
            else if (data.type === 'messages_read') {
                setMessages(prev => prev.map(m => 
                    m.conversation_id === data.conversation_id && m.sender_id === user?.id
                        ? { ...m, is_read: true }
                        : m
                ));
            }
        };
        
        ws.onclose = () => {
            console.log('Chat WebSocket disconnected');
        };
        
        // Ping every 30s
        const pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'ping' }));
            }
        }, 30000);
        
        return () => {
            clearInterval(pingInterval);
            ws.close();
        };
    }, [token, selectedContact, user?.id]);
    
    // Fetch contacts on mount
    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);
    
    // Fetch messages when contact selected
    useEffect(() => {
        if (selectedContact) {
            fetchMessages(selectedContact.id);
            if (isMobileView) {
                setShowContactList(false);
            }
        }
    }, [selectedContact, fetchMessages, isMobileView]);
    
    // Send typing indicator
    const handleTyping = () => {
        if (!wsRef.current || !selectedContact) return;
        
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        
        if (wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'typing',
                recipient_id: selectedContact.id
            }));
        }
        
        typingTimeoutRef.current = setTimeout(() => {}, 2000);
    };
    
    // Send message
    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedContact) return;
        
        try {
            const res = await fetch(`${API_URL}/api/chat/send`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    recipient_id: selectedContact.id,
                    content: newMessage.trim()
                })
            });
            
            if (res.ok) {
                const data = await res.json();
                setMessages(prev => [...prev, data.message]);
                setNewMessage('');
                // Update contact's last message
                setContacts(prev => prev.map(c => 
                    c.id === selectedContact.id
                        ? { ...c, last_message: newMessage.trim(), last_message_at: new Date().toISOString() }
                        : c
                ));
            } else {
                toast.error('Erreur lors de l\'envoi');
            }
        } catch (err) {
            console.error('Error sending message:', err);
            toast.error('Erreur de connexion');
        }
    };
    
    // Handle file upload
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedContact) return;
        
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 10 Mo)');
            return;
        }
        
        setIsUploading(true);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const uploadRes = await fetch(`${API_URL}/api/chat/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            
            if (!uploadRes.ok) throw new Error('Upload failed');
            
            const uploadData = await uploadRes.json();
            
            const res = await fetch(`${API_URL}/api/chat/send`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    recipient_id: selectedContact.id,
                    content: '',
                    attachment_type: uploadData.attachment_type,
                    attachment_url: uploadData.attachment_url,
                    attachment_name: uploadData.attachment_name
                })
            });
            
            if (res.ok) {
                const data = await res.json();
                setMessages(prev => [...prev, data.message]);
                toast.success('Fichier envoyé');
            }
        } catch (err) {
            console.error('Error uploading:', err);
            toast.error('Erreur lors de l\'envoi');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };
    
    // Handle voice recording complete
    const handleVoiceRecordingComplete = async (audioBlob, duration) => {
        if (!selectedContact || !audioBlob) return;
        
        setIsUploading(true);
        
        try {
            const formData = new FormData();
            formData.append('file', audioBlob, `voice_${Date.now()}.webm`);
            
            const uploadRes = await fetch(`${API_URL}/api/chat/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            
            if (!uploadRes.ok) throw new Error('Upload failed');
            
            const uploadData = await uploadRes.json();
            
            const res = await fetch(`${API_URL}/api/chat/send`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    recipient_id: selectedContact.id,
                    content: '',
                    attachment_type: 'voice',
                    attachment_url: uploadData.attachment_url,
                    attachment_name: `Message vocal (${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')})`
                })
            });
            
            if (res.ok) {
                const data = await res.json();
                setMessages(prev => [...prev, data.message]);
                toast.success('Message vocal envoyé');
            }
        } catch (err) {
            console.error('Error sending voice:', err);
            toast.error('Erreur lors de l\'envoi du message vocal');
        } finally {
            setIsUploading(false);
        }
    };
    
    // Select contact
    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        setContacts(prev => prev.map(c => 
            c.id === contact.id ? { ...c, unread_count: 0 } : c
        ));
    };
    
    // Back to contact list (mobile)
    const handleBackToList = () => {
        setShowContactList(true);
        setSelectedContact(null);
    };
    
    // Format time
    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;
        
        if (diff < 60000) return 'À l\'instant';
        if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
        if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    };
    
    // Format date header
    const formatDateHeader = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
        if (date.toDateString() === yesterday.toDateString()) return "Hier";
        return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    };
    
    // Group messages by date
    const groupedMessages = messages.reduce((acc, msg) => {
        const date = new Date(msg.created_at).toDateString();
        if (!acc[date]) acc[date] = [];
        acc[date].push(msg);
        return acc;
    }, {});
    
    // Render message
    const renderMessage = (msg) => {
        const isOwn = msg.sender_id === user?.id;
        
        return (
            <div
                key={msg.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}
            >
                <div
                    className={`max-w-[70%] p-3 rounded-2xl ${
                        isOwn
                            ? 'bg-primary text-primary-foreground rounded-br-md'
                            : 'bg-muted rounded-bl-md'
                    }`}
                >
                    {/* Voice message */}
                    {msg.attachment_type === 'voice' && (
                        <VoiceMessagePlayer 
                            src={msg.attachment_url}
                            isOwn={isOwn}
                        />
                    )}
                    
                    {/* Image */}
                    {msg.attachment_type === 'image' && (
                        <img 
                            src={msg.attachment_url}
                            alt={msg.attachment_name}
                            className="max-w-full rounded-lg mb-2 cursor-pointer hover:opacity-90"
                            onClick={() => window.open(msg.attachment_url, '_blank')}
                        />
                    )}
                    
                    {/* File */}
                    {msg.attachment_type === 'file' && (
                        <a 
                            href={msg.attachment_url}
                            download={msg.attachment_name}
                            className={`flex items-center gap-2 p-2 rounded-lg mb-2 ${
                                isOwn ? 'bg-primary-foreground/10' : 'bg-background/50'
                            }`}
                        >
                            <File className="w-5 h-5" />
                            <span className="text-sm truncate flex-1">{msg.attachment_name}</span>
                            <Download className="w-4 h-4" />
                        </a>
                    )}
                    
                    {/* Text content with TTS and clickable links */}
                    {msg.content && (
                        <div className="flex items-start gap-1">
                            <p className="text-sm whitespace-pre-wrap flex-1">
                                {linkifyText(msg.content, isOwn 
                                    ? "text-blue-200 underline hover:text-blue-100 break-all" 
                                    : "text-blue-600 underline hover:text-blue-500 break-all"
                                )}
                            </p>
                            <TextToSpeechButton text={msg.content} />
                        </div>
                    )}
                    
                    <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                        <span className="text-[10px] opacity-70">
                            {formatTime(msg.created_at)}
                        </span>
                        {isOwn && (
                            msg.is_read 
                                ? <CheckCheck className="w-3 h-3 opacity-70" />
                                : <Check className="w-3 h-3 opacity-70" />
                        )}
                    </div>
                </div>
            </div>
        );
    };
    
    return (
        <Layout>
            <div className="h-[calc(100vh-80px)] lg:h-[calc(100vh-32px)] flex flex-col" data-testid="messages-page">
                <div className="flex-1 flex overflow-hidden rounded-lg border border-border bg-card">
                    {/* Contact list */}
                    {(!isMobileView || showContactList) && (
                        <div className={`${isMobileView ? 'w-full' : 'w-80'} border-r border-border flex flex-col`}>
                            {/* Header */}
                            <div className="p-4 border-b border-border">
                                <h2 className="text-lg font-semibold flex items-center gap-2">
                                    <MessageCircle className="w-5 h-5" />
                                    Messages
                                </h2>
                                <div className="mt-3 relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Rechercher..."
                                        className="pl-9"
                                    />
                                </div>
                            </div>
                            
                            {/* Contact list */}
                            <ScrollArea className="flex-1">
                                {filteredContacts.length === 0 ? (
                                    <div className="p-6 text-center text-muted-foreground">
                                        <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">
                                            {searchQuery ? 'Aucun résultat' : 'Aucun contact'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-border">
                                        {filteredContacts.map((contact) => (
                                            <button
                                                key={contact.id}
                                                onClick={() => handleSelectContact(contact)}
                                                className={`w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left ${
                                                    selectedContact?.id === contact.id ? 'bg-muted' : ''
                                                }`}
                                                data-testid={`contact-${contact.id}`}
                                            >
                                                <div className="relative">
                                                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                                        <span className="text-primary font-semibold text-lg">
                                                            {contact.name?.charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                    {onlineUsers.includes(contact.id) && (
                                                        <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-card" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium truncate">{contact.name}</span>
                                                        {contact.last_message_at && (
                                                            <span className="text-xs text-muted-foreground">
                                                                {formatTime(contact.last_message_at)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge className={`text-xs ${ROLE_COLORS[contact.role] || 'bg-gray-100'}`}>
                                                            {ROLE_LABELS[contact.role] || contact.role}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-1">
                                                        <span className="text-xs text-muted-foreground truncate">
                                                            {contact.last_message_is_mine && 'Vous: '}
                                                            {contact.last_message || 'Commencez une conversation'}
                                                        </span>
                                                        {contact.unread_count > 0 && (
                                                            <Badge variant="destructive" className="text-xs h-5 min-w-5 justify-center">
                                                                {contact.unread_count}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </ScrollArea>
                        </div>
                    )}
                    
                    {/* Chat area */}
                    {(!isMobileView || !showContactList) && (
                        <div className="flex-1 flex flex-col">
                            {selectedContact ? (
                                <>
                                    {/* Chat header */}
                                    <div className="p-4 border-b border-border flex items-center gap-3">
                                        {isMobileView && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={handleBackToList}
                                            >
                                                <ArrowLeft className="w-5 h-5" />
                                            </Button>
                                        )}
                                        <div className="relative">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                <span className="text-primary font-medium">
                                                    {selectedContact.name?.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            {onlineUsers.includes(selectedContact.id) && (
                                                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-card" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold">{selectedContact.name}</h3>
                                            <div className="flex items-center gap-2">
                                                <Badge className={`text-xs ${ROLE_COLORS[selectedContact.role] || 'bg-gray-100'}`}>
                                                    {ROLE_LABELS[selectedContact.role] || selectedContact.role}
                                                </Badge>
                                                {onlineUsers.includes(selectedContact.id) ? (
                                                    <span className="text-xs text-green-600 flex items-center gap-1">
                                                        <Circle className="w-2 h-2 fill-current" /> En ligne
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">Hors ligne</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Messages */}
                                    <ScrollArea className="flex-1 p-4">
                                        {Object.entries(groupedMessages).map(([date, msgs]) => (
                                            <div key={date}>
                                                <div className="flex justify-center mb-4">
                                                    <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                                                        {formatDateHeader(msgs[0].created_at)}
                                                    </span>
                                                </div>
                                                {msgs.map(renderMessage)}
                                            </div>
                                        ))}
                                        
                                        {isTyping && (
                                            <div className="flex justify-start mb-3">
                                                <div className="bg-muted p-3 rounded-2xl rounded-bl-md">
                                                    <div className="flex gap-1">
                                                        <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                        <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                        <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </ScrollArea>
                                    
                                    {/* Input */}
                                    <div className="p-4 border-t border-border">
                                        <div className="flex items-center gap-3">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                className="hidden"
                                                onChange={handleFileUpload}
                                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                            />
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={isUploading}
                                            >
                                                {isUploading ? (
                                                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <Paperclip className="w-5 h-5" />
                                                )}
                                            </Button>
                                            <VoiceRecorder 
                                                onRecordingComplete={handleVoiceRecordingComplete}
                                                maxDuration={300}
                                                disabled={isUploading}
                                            />
                                            <Input
                                                value={newMessage}
                                                onChange={(e) => {
                                                    setNewMessage(e.target.value);
                                                    handleTyping();
                                                }}
                                                placeholder="Écrivez votre message..."
                                                className="flex-1"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSendMessage();
                                                    }
                                                }}
                                            />
                                            <Button
                                                onClick={handleSendMessage}
                                                disabled={!newMessage.trim()}
                                            >
                                                <Send className="w-5 h-5" />
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                                    <div className="text-center">
                                        <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                        <h3 className="text-lg font-medium mb-2">Vos messages</h3>
                                        <p className="text-sm">
                                            Sélectionnez un contact pour commencer une conversation
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Messages;
